import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Link2, Copy, ShieldCheck, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { HudButton } from '../hud';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const STATUS_LABELS = {
  pending:   { label: 'En attente',  color: 'rgba(234,179,8,1)',  bg: 'rgba(234,179,8,0.12)' },
  approved:  { label: 'Approuvé',    color: 'rgba(34,197,94,1)',  bg: 'rgba(34,197,94,0.14)' },
  commented: { label: 'Commenté',    color: 'rgba(59,130,246,1)', bg: 'rgba(59,130,246,0.14)' },
  refused:   { label: 'Refusé',      color: 'rgba(239,68,68,1)',  bg: 'rgba(239,68,68,0.14)' },
};

/**
 * RenderShareDialog — modale "Partager au client" pour un rendu.
 * Récupère/crée un token via POST /api/projects/{pid}/generations/{job_id}/share-token,
 * affiche le lien `/share/:token`, le statut TMA actuel et permet de révoquer.
 */
const RenderShareDialog = ({ projectId, item, onClose }) => {
  const [data, setData] = useState(null); // {share_token, public_url, client_status, ...}
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const create = async () => {
      try {
        const r = await axios.post(
          `${API}/projects/${projectId}/generations/${item.job_id}/share-token`,
          {},
          opts,
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Impossible de générer le lien');
        if (!cancelled) onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    create();
    return () => { cancelled = true; };
  }, [projectId, item.job_id, onClose]);

  const fullPublicUrl = data
    ? `${window.location.origin}${data.public_url || `/share/${data.share_token}`}`
    : '';

  const copy = async () => {
    if (!fullPublicUrl) return;
    try {
      await navigator.clipboard.writeText(fullPublicUrl);
      toast.success('Lien copié dans le presse-papier');
    } catch {
      toast.error('Copie impossible');
    }
  };

  const revoke = async () => {
    setRevoking(true);
    try {
      await axios.delete(
        `${API}/projects/${projectId}/generations/${item.job_id}/share-token`,
        opts,
      );
      toast.success('Lien révoqué — la vue publique est désactivée');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec révocation');
    } finally {
      setRevoking(false);
    }
  };

  const statusMeta = STATUS_LABELS[data?.client_status || 'pending'];

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="render-share-dialog"
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(6,8,12,0.86)', backdropFilter: 'blur(14px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'var(--c-surface-1)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--c-border)' }}
        >
          <div className="flex items-center gap-2.5">
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--c-gold-soft)', border: '1px solid var(--c-border-accent)' }}
            >
              <Link2 className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
            </span>
            <div>
              <span className="hud-eyebrow hud-eyebrow-gold">Vue publique</span>
              <p
                className="font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 16 }}
              >
                Partager au client
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="render-share-close"
            aria-label="Fermer"
            className="rounded-lg p-1.5 transition-colors"
            style={{ color: 'var(--c-text-2)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-gold)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-2)'; }}
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="hud-badge"
              style={{
                background: 'var(--c-surface-2)',
                color: 'var(--c-text)',
                border: '1px solid var(--c-border)',
                fontSize: 10,
                padding: '2px 7px',
                letterSpacing: 0.4,
              }}
            >
              Lot {item.lot_number || item.lot_id?.slice(0, 8)}
              {item.typology ? ` · ${item.typology}` : ''}
            </span>
            <span
              className="hud-badge"
              data-testid="render-share-status"
              style={{
                background: statusMeta.bg,
                color: statusMeta.color,
                border: `1px solid ${statusMeta.color}55`,
                fontSize: 10,
                padding: '2px 7px',
                letterSpacing: 0.4,
              }}
            >
              <ShieldCheck className="w-2.5 h-2.5" />
              TMA · {statusMeta.label}
            </span>
          </div>

          <p className="text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
            Un lien public unique a été {data?.recycled ? 'récupéré' : 'généré'} pour ce rendu.
            Le client accède à une page <strong>Smooth</strong> dédiée et peut <strong>approuver</strong>,
            <strong> commenter</strong> ou <strong>refuser</strong> directement la maquette.
          </p>

          <div
            className="rounded-xl p-3"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
          >
            {loading ? (
              <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Génération du lien sécurisé…
              </div>
            ) : (
              <>
                <span className="hud-eyebrow">URL publique</span>
                <p
                  className="mt-1 break-all text-[12.5px] font-mono select-all"
                  style={{ color: 'var(--c-text)' }}
                  data-testid="render-share-url"
                >
                  {fullPublicUrl}
                </p>
              </>
            )}
          </div>

          {data && data.client_signed_by && (
            <div
              className="rounded-lg p-3 text-[11.5px]"
              style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}
            >
              Signé par <span style={{ color: 'var(--c-text)' }}>{data.client_signed_by}</span>
              {data.client_comment ? (
                <span> — “{data.client_comment}”</span>
              ) : null}
            </div>
          )}

          <div className="flex items-center gap-2">
            <HudButton
              variant="primary"
              size="sm"
              className="flex-1 justify-center"
              onClick={copy}
              disabled={loading}
              leftIcon={<Copy className="w-3.5 h-3.5" />}
              data-testid="render-share-copy"
            >
              Copier le lien
            </HudButton>
            <a
              href={fullPublicUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="render-share-open"
              className={loading ? 'pointer-events-none opacity-50' : ''}
            >
              <HudButton variant="secondary" size="sm" leftIcon={<ExternalLink className="w-3.5 h-3.5" />}>
                Ouvrir
              </HudButton>
            </a>
          </div>

          <button
            type="button"
            onClick={revoke}
            disabled={loading || revoking}
            data-testid="render-share-revoke"
            className="w-full text-[11px] py-1.5 rounded-md transition-colors disabled:opacity-40"
            style={{ color: 'var(--c-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#FCA5A5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-muted)'; }}
          >
            {revoking ? <RefreshCw className="w-3 h-3 inline animate-spin mr-1" /> : null}
            Révoquer le lien
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenderShareDialog;
